/**
 * Lightweight tooltip using a portal to escape overflow:hidden containers.
 * Supports hover (desktop) and tap-to-toggle (mobile) with tap-outside-to-dismiss.
 *
 * When `asChild` is true, event handlers are merged onto the child element via
 * cloneElement instead of wrapping in a <span>. The child must be a single
 * React element that accepts ref, onClick, onMouseEnter, onMouseLeave, onFocus,
 * and onBlur props.
 */
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  isValidElement,
  cloneElement,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  /** When true, merges handlers onto the child element instead of wrapping in a span */
  asChild?: boolean;
}

export function Tooltip({ text, children, asChild = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLElement>(null);

  const updateCoords = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 6,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  const show = useCallback(() => {
    updateCoords();
    setVisible(true);
  }, [updateCoords]);

  const hide = useCallback(() => setVisible(false), []);

  const toggle = useCallback(() => {
    if (visible) {
      setVisible(false);
    } else {
      updateCoords();
      setVisible(true);
    }
  }, [visible, updateCoords]);

  // Close on tap outside (mobile)
  useEffect(() => {
    if (!visible) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [visible]);

  const tooltipPortal =
    visible &&
    createPortal(
      <div
        className="fixed z-[100] px-2.5 py-1.5 text-xs text-white bg-gray-900 border border-white/10 rounded-lg shadow-lg max-w-[250px] pointer-events-none -translate-x-1/2 whitespace-pre-line"
        style={{ top: coords.top, left: coords.left }}
      >
        {text}
      </div>,
      document.body
    );

  if (asChild && isValidElement(children)) {
    const child = children as React.ReactElement<Record<string, unknown>>;
    const childProps = child.props;

    const composeHandler = (
      ours: (...args: unknown[]) => void,
      propName: string
    ): ((...args: unknown[]) => void) => {
      const theirs = childProps[propName] as ((...a: unknown[]) => void) | undefined;
      if (!theirs) return ours;
      return (...args: unknown[]) => {
        theirs(...args);
        ours(...args);
      };
    };

    // React 18: ref is on element.ref, not element.props.ref
    // React 19+: ref is a regular prop
    const childRef = ((child as unknown as { ref?: Ref<HTMLElement> }).ref ?? childProps.ref) as
      | Ref<HTMLElement>
      | undefined;
    const composedRef = (node: HTMLElement | null) => {
      (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object') {
        (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    };

    return (
      <>
        {cloneElement(child, {
          ref: composedRef,
          onMouseEnter: composeHandler(show, 'onMouseEnter'),
          onMouseLeave: composeHandler(hide, 'onMouseLeave'),
          onFocus: composeHandler(show, 'onFocus'),
          onBlur: composeHandler(hide, 'onBlur'),
          onClick: composeHandler(toggle, 'onClick'),
        })}
        {tooltipPortal}
      </>
    );
  }

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={toggle}
      className="inline-flex"
    >
      {children}
      {tooltipPortal}
    </span>
  );
}
